/**
 * Web Vitals Monitoring
 * Collects Core Web Vitals metrics and sends to analytics
 */

export interface WebVitalsMetrics {
  fcp?: number;  // First Contentful Paint (ms)
  lcp?: number;  // Largest Contentful Paint (ms)
  fid?: number;  // First Input Delay (ms)
  cls?: number;  // Cumulative Layout Shift (unitless)
  ttfb?: number; // Time to First Byte (ms)
}

const metrics: WebVitalsMetrics = {};

const budgets = {
  fcp: 1800,   // First Contentful Paint (ms)
  lcp: 2500,   // Largest Contentful Paint (ms)
  fid: 100,    // First Input Delay (ms)
  cls: 0.25,   // Cumulative Layout Shift
  ttfb: 600,   // Time to First Byte (ms)
};

/**
 * Check metric against budget and log warning if exceeded.
 * Uses console.warn for budget violations so they surface in monitoring tools.
 */
function checkBudget(metric: string, value: number, budget: number) {
  // CLS is unitless (not milliseconds), format with decimals
  const isCLS = metric === 'CLS';
  const formatted = isCLS ? value.toFixed(3) : value.toFixed(0);
  const budgetFormatted = isCLS ? budget.toFixed(3) : String(budget);
  const unit = isCLS ? '' : 'ms';

  if (value <= budget) {
    console.log(`[WebVitals] ${metric}: ${formatted}${unit} (budget: ${budgetFormatted}${unit})`);
  } else {
    console.warn(`[WebVitals] ${metric} OVER BUDGET: ${formatted}${unit} / ${budgetFormatted}${unit} (+${isCLS ? (value - budget).toFixed(3) : (value - budget).toFixed(0)}${unit})`);
  }
}

/**
 * Initialize Web Vitals collection
 * Call once on app startup
 */
export function initializeWebVitals() {
  // Check if Web Vitals API available (modern browsers)
  if ('PerformanceObserver' in window) {
    try {
      // Track FCP (First Contentful Paint)
      const fcpObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            metrics.fcp = entry.startTime;
            checkBudget('FCP', metrics.fcp, budgets.fcp);
          }
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });

      // Track LCP (Largest Contentful Paint)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        metrics.lcp = (lastEntry as any).renderTime || (lastEntry as any).loadTime;
        checkBudget('LCP', metrics.lcp!, budgets.lcp);
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      // Track CLS (Cumulative Layout Shift)
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
            metrics.cls = clsValue;
            checkBudget('CLS', metrics.cls, budgets.cls);
          }
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });

      // Track FID (First Input Delay)
      const fidObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const e = entry as any;
          metrics.fid = e.processingStart - e.startTime;
          checkBudget('FID', metrics.fid!, budgets.fid);
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });

      console.log('[WebVitals] Monitoring initialized');
    } catch (err) {
      console.warn('Web Vitals initialization failed:', err);
    }
  }

  // Get TTFB from Navigation Timing API
  if ('performance' in window && 'getEntriesByType' in performance) {
    try {
      const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navTiming) {
        metrics.ttfb = navTiming.responseStart - navTiming.fetchStart;
        checkBudget('TTFB', metrics.ttfb, budgets.ttfb);
      }
    } catch (err) {
      console.warn('[WebVitals] TTFB collection failed:', err);
    }
  }
}

/**
 * Get current metrics snapshot
 */
export function getWebVitals(): WebVitalsMetrics {
  return { ...metrics };
}
